"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  type CopySubscription,
  type CopySubscriptionFilters,
} from "@/lib/api";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { formatMoney } from "@/lib/format";

interface SubscribeToPiModalProps {
  piUsername: string;
  // If present, modal opens in "edit" mode against the existing
  // subscription. Otherwise it creates a new one.
  existing?: CopySubscription | null;
  onClose: () => void;
  onSuccess: (sub: CopySubscription) => void;
}

const ALLOC_PRESETS = [50, 100, 250, 500, 1000];

/**
 * EPIC-005 STORY-503 — Subscribe to PI / edit subscription modal.
 *
 * Two modes (driven by `existing`):
 *   - new subscription: POST /v1/me/copy-subscriptions
 *   - edit existing: PATCH /v1/me/copy-subscriptions/:id
 *
 * Form covers:
 *   - allocation in R$ (presets + custom input)
 *   - price-band filter (min/max ¢ — default unset = all prices)
 *   - outcome filter (yes / no / both — default both)
 *
 * Filter complexity is intentionally bounded — STORY-508 (subscription
 * management UI) lands a fuller per-subscription analytics + edit
 * experience. Today's modal is the minimum viable subscribe flow.
 */
export function SubscribeToPiModal({
  piUsername,
  existing,
  onClose,
  onSuccess,
}: SubscribeToPiModalProps) {
  const { t } = useTranslation();
  const displayCurrency = useDisplayCurrency();
  const isEdit = Boolean(existing);
  const initialAllocReais = existing
    ? Number(existing.allocationCents) / 100
    : 100;

  const [allocReais, setAllocReais] = useState<number>(initialAllocReais);
  const [customAlloc, setCustomAlloc] = useState<string>("");
  const [minPriceCents, setMinPriceCents] = useState<string>(
    existing?.filters.minPriceCents?.toString() ?? "",
  );
  const [maxPriceCents, setMaxPriceCents] = useState<string>(
    existing?.filters.maxPriceCents?.toString() ?? "",
  );
  const [outcomeMode, setOutcomeMode] = useState<"both" | "yes" | "no">(
    (() => {
      const k = existing?.filters.outcomeKinds;
      if (!k || k.length === 0 || k.length === 2) return "both";
      return k[0];
    })(),
  );
  // EPIC-COPY STORY-510 — per-subscription stop-loss threshold.
  // null on existing means "use platform_config default" (now 30 as
  // of migration 0167, 2026-05-19 founder review; was 50 in the
  // initial 0077 seed). We default the slider to 30 here so the UI
  // matches the backend behavior; saving with the slider untouched
  // sends null (preserving the "use default" semantic) so a future
  // founder change to copy.stop_loss_default_pct flows through
  // without manual per-subscription edits.
  const STOP_LOSS_DEFAULT = 30;
  const initialStopLoss = existing?.stopLossPct ?? STOP_LOSS_DEFAULT;
  const [stopLossPct, setStopLossPct] = useState<number>(initialStopLoss);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  async function handleSubmit() {
    setError(null);
    const allocationCents = Math.round(allocReais * 100);
    if (!Number.isInteger(allocationCents) || allocationCents < 1000) {
      setError(t("subscribeToPi.errMinAlloc"));
      return;
    }
    if (allocationCents > 100_000_000) {
      setError(t("subscribeToPi.errMaxAlloc"));
      return;
    }

    const filters: CopySubscriptionFilters = {};
    if (minPriceCents.trim()) {
      const n = parseInt(minPriceCents, 10);
      if (!Number.isInteger(n) || n < 1 || n > 99) {
        setError(t("subscribeToPi.errMinPrice"));
        return;
      }
      filters.minPriceCents = n;
    }
    if (maxPriceCents.trim()) {
      const n = parseInt(maxPriceCents, 10);
      if (!Number.isInteger(n) || n < 1 || n > 99) {
        setError(t("subscribeToPi.errMaxPrice"));
        return;
      }
      filters.maxPriceCents = n;
    }
    if (
      filters.minPriceCents !== undefined &&
      filters.maxPriceCents !== undefined &&
      filters.minPriceCents > filters.maxPriceCents
    ) {
      setError(t("subscribeToPi.errMinExceedsMax"));
      return;
    }
    if (outcomeMode === "yes") filters.outcomeKinds = ["yes"];
    else if (outcomeMode === "no") filters.outcomeKinds = ["no"];

    if (
      !Number.isInteger(stopLossPct) ||
      stopLossPct < 5 ||
      stopLossPct > 95
    ) {
      setError(t("subscribeToPi.errStopLossRange"));
      return;
    }

    setSubmitting(true);
    try {
      let sub = isEdit
        ? await api.updateCopySubscription(existing!.id, {
            allocationCents,
            filters,
          })
        : await api.subscribeToPi({
            piUsername,
            allocationCents,
            filters,
          });

      // EPIC-COPY STORY-510 — apply stop-loss override when it
      // differs from the current row state. We send `null` when
      // the user picked the platform default (30 as of 2026-05-19)
      // so the row's stop_loss_pct stays NULL and inherits future
      // default changes. Otherwise we send the integer.
      const currentRowPct = sub.stopLossPct ?? STOP_LOSS_DEFAULT;
      if (stopLossPct !== currentRowPct) {
        const wantPct =
          stopLossPct === STOP_LOSS_DEFAULT ? null : stopLossPct;
        try {
          await api.setCopyStopLoss(sub.id, wantPct);
          // Reflect the saved value in the response shape.
          sub = { ...sub, stopLossPct: wantPct };
        } catch (slErr) {
          // Stop-loss save failed but the subscription itself
          // saved — surface the partial-success state cleanly so
          // the user can retry just the threshold edit.
          const msg =
            slErr instanceof ApiError
              ? slErr.message
              : slErr instanceof Error
                ? slErr.message
                : t("subscribeToPi.errStopLossSave");
          setError(msg);
          // Don't bail — proceed with onSuccess for the saved
          // allocation/filters; the threshold can be edited again.
        }
      }

      onSuccess(sub);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("subscribeToPi.errSaveFailed");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (typeof document === "undefined") return null;
  // Portal to <body> so a backdrop-filter ancestor (SiteHeader) can't
  // re-root our `fixed inset-0` containing block.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscribe-pi-title"
      className="fixed inset-0 z-50 flex items-end justify-center px-3 py-4 sm:items-center"
    >
      <button
        type="button"
        aria-label={t("subscribeToPi.dismiss")}
        onClick={() => {
          if (!submitting) onClose();
        }}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-xl">
        <h2
          id="subscribe-pi-title"
          className="text-[14px] font-bold text-[var(--color-text)]"
        >
          {isEdit
            ? t("subscribeToPi.editTitle")
            : t("subscribeToPi.copyTitle")}{" "}
          <span style={{ color: "var(--color-brand)" }}>{piUsername}</span>
        </h2>
        <p className="mt-0.5 text-[12px] text-[var(--color-text3)]">
          {isEdit
            ? t("subscribeToPi.editSubtitle")
            : t("subscribeToPi.newSubtitle")}
        </p>

        {/* Allocation */}
        <div className="mt-4">
          <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--color-text3)]">
            {t("subscribeToPi.allocationLabel")}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {ALLOC_PRESETS.map((r) => {
              const active =
                customAlloc === "" && Math.round(allocReais) === r;
              return (
                <button
                  key={r}
                  type="button"
                  disabled={submitting}
                  onClick={() => {
                    setAllocReais(r);
                    setCustomAlloc("");
                  }}
                  className={`rounded-md border px-3 py-1.5 text-[12px] font-bold transition-colors ${
                    active
                      ? "border-[var(--color-brand)] bg-[var(--color-brand-tint)] text-[var(--color-brand)]"
                      : "border-[var(--color-border)] text-[var(--color-text2)] hover:border-[var(--color-brand)]"
                  }`}
                >
                  {formatMoney(r * 100, displayCurrency)}
                </button>
              );
            })}
          </div>
          <input
            type="number"
            inputMode="decimal"
            min={10}
            max={1_000_000}
            step={1}
            placeholder={t("subscribeToPi.customAllocPlaceholder")}
            value={customAlloc}
            onChange={(e) => {
              setCustomAlloc(e.target.value);
              const n = parseFloat(e.target.value);
              if (Number.isFinite(n)) setAllocReais(n);
            }}
            disabled={submitting}
            className="mt-2 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
          />
          {existing && (
            <p className="mt-1 text-[11px] text-[var(--color-text3)]">
              {t("subscribeToPi.currentlyCommittedTemplate", {
                amount: formatMoney(existing.committedCents, displayCurrency),
              })}
            </p>
          )}
        </div>

        {/* EPIC-COPY STORY-510 — stop-loss threshold slider. */}
        <div className="mt-4">
          <div className="flex items-baseline justify-between">
            <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--color-text3)]">
              {t("subscribeToPi.stopLossLabel")}
            </div>
            <div className="text-[12px] font-mono font-bold text-[var(--color-text)]">
              {stopLossPct}%
              {existing?.stopLossPct === null && (
                <span className="ml-1.5 text-[10px] font-normal text-[var(--color-text3)]">
                  ({t("subscribeToPi.stopLossDefault")})
                </span>
              )}
            </div>
          </div>
          <input
            type="range"
            min={5}
            max={95}
            step={1}
            value={stopLossPct}
            onChange={(e) => setStopLossPct(parseInt(e.target.value, 10))}
            disabled={submitting}
            aria-label={t("subscribeToPi.stopLossLabel")}
            className="mt-2 w-full cursor-pointer accent-[var(--color-brand)]"
          />
          <p className="mt-1 text-[10px] text-[var(--color-text3)]">
            {t("subscribeToPi.stopLossHelp")}
          </p>
          {existing?.stopLossTriggeredAt && (
            <p
              className="mt-1 text-[10px] font-bold"
              style={{ color: "var(--color-no)" }}
            >
              {t("subscribeToPi.stopLossTriggeredNotice")}
            </p>
          )}
        </div>

        {/* Outcome filter */}
        <div className="mt-4">
          <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--color-text3)]">
            {t("subscribeToPi.sidesToCopy")}
          </div>
          <div className="mt-1.5 flex gap-1.5">
            {(
              [
                ["both", t("subscribeToPi.outcomeBoth")],
                ["yes", t("subscribeToPi.outcomeYes")],
                ["no", t("subscribeToPi.outcomeNo")],
              ] as const
            ).map(([val, lbl]) => (
              <button
                key={val}
                type="button"
                disabled={submitting}
                onClick={() => setOutcomeMode(val)}
                className={`flex-1 rounded-md border py-1.5 text-[11px] font-bold transition-colors ${
                  outcomeMode === val
                    ? "border-[var(--color-brand)] bg-[var(--color-brand-tint)] text-[var(--color-brand)]"
                    : "border-[var(--color-border)] text-[var(--color-text2)] hover:border-[var(--color-brand)]"
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {/* Price band */}
        <div className="mt-4">
          <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--color-text3)]">
            {t("subscribeToPi.priceBandLabel")}
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={99}
              placeholder={t("subscribeToPi.minPlaceholder")}
              value={minPriceCents}
              onChange={(e) => setMinPriceCents(e.target.value)}
              disabled={submitting}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
            />
            <span className="text-[11px] text-[var(--color-text3)]">
              {t("subscribeToPi.priceBandTo")}
            </span>
            <input
              type="number"
              min={1}
              max={99}
              placeholder={t("subscribeToPi.maxPlaceholder")}
              value={maxPriceCents}
              onChange={(e) => setMaxPriceCents(e.target.value)}
              disabled={submitting}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
            />
          </div>
          <p className="mt-1 text-[11px] text-[var(--color-text3)]">
            {t("subscribeToPi.priceBandHelp")}
          </p>
        </div>

        {error && (
          <p
            role="alert"
            className="mt-3 text-[12px]"
            style={{ color: "var(--color-no)" }}
          >
            {error}
          </p>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            disabled={submitting}
            onClick={onClose}
            className="min-h-[44px] flex-1 rounded-md border border-[var(--color-border)] py-2 text-[12px] font-bold text-[var(--color-text2)] transition-colors hover:border-[var(--color-brand)] md:min-h-0"
          >
            {t("subscribeToPi.cancel")}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={handleSubmit}
            className="min-h-[44px] flex-1 rounded-md py-2 text-[12px] font-bold text-white transition-colors disabled:opacity-60 md:min-h-0"
            style={{ background: "var(--color-brand)" }}
          >
            {submitting
              ? t("subscribeToPi.saving")
              : isEdit
                ? t("subscribeToPi.save")
                : t("subscribeToPi.copyTemplate", { username: piUsername })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
